/**
 * AssetDrawerContent - Shared presentation for asset drawer UI
 * Used by standalone AssetDrawer (with portal/backdrop) and WorkflowAssetDrawer (inline)
 */

import { useId, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { X, Search, Upload, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ViewModeToggle,
  type ViewMode,
} from "@/components/shared/ViewModeToggle";

// Shared accent tokens for visuals and audio - exported for use by AssetDrawer shell
export const ACCENT_COLORS = {
  violet: "var(--color-primitive-violet-500)",
  cobalt: "var(--color-primitive-blue-500)",
} as const;

export interface TypeTag {
  id: string;
  label: string;
}

export interface UploadConfig {
  label: string;
  sublabel?: string;
  onUpload?: (files: FileList) => void;
  onGenerate?: () => void;
  generateLabel?: string;
}

/** Props for hide existing toggle - both must be provided together or neither */
export type HideExistingProps =
  | {
      hideExisting?: undefined;
      onHideExistingChange?: undefined;
      hideExistingLabel?: undefined;
    }
  | {
      hideExisting: boolean;
      onHideExistingChange: (hide: boolean) => void;
      hideExistingLabel?: string;
    };

/** Props for view mode toggle - both must be provided together or neither */
export type ViewModeProps =
  | { viewMode?: undefined; onViewModeChange?: undefined }
  | { viewMode: ViewMode; onViewModeChange: (mode: ViewMode) => void };

/** Base props for AssetDrawerContent */
interface AssetDrawerContentBaseProps {
  /** Scene context label (e.g., "1 · SCENE 1") */
  sceneLabel?: string;
  /** Main title (e.g., "Add to Input Visuals") */
  title: string;
  /** Description text below title */
  description?: string;
  /** Header layout - 'stacked' (default) or 'inline' for single-line scene label + title */
  headerLayout?: "stacked" | "inline";
  /** Called when close button is clicked */
  onClose: () => void;
  /** Accent color - 'violet' for visuals, 'cobalt' for audio */
  accentColor?: "violet" | "cobalt";
  /** Type tags to show (e.g., visual types or audio types). Empty array or omit to hide. */
  typeTags?: TypeTag[];
  /** Currently selected type tag */
  selectedType?: string;
  /** Called when type tag is selected */
  onTypeSelect?: (type: string) => void;
  /** Upload box config (optional - standalone uses this) */
  uploadConfig?: UploadConfig;
  /** Search placeholder text */
  searchPlaceholder?: string;
  /** Current search value */
  searchValue: string;
  /** Called when search value changes */
  onSearchChange: (value: string) => void;
  /** Render prop for search row actions (e.g., workflow upload button) */
  renderSearchActions?: () => ReactNode;
  /** Render prop for bulk actions row (e.g., select all / clear selection) */
  renderBulkActions?: () => ReactNode;
  /** Render prop for filter dropdowns (renders between search bar and assets) */
  renderFilters?: () => ReactNode;
  /** Number of selected items */
  selectedCount: number;
  /** Called when Add to scene is clicked */
  onAdd: () => void;
  /** Custom add button label - can be a string or function that receives selectedCount */
  addButtonLabel?: string | ((count: number) => string);
  /** Whether to show '+' prefix on add button (default true) */
  showAddButtonPrefix?: boolean;
  /** Children rendered in the scrollable assets area */
  children: ReactNode;
  /** Optional ID for aria-labelledby association */
  titleId?: string;
}

/** Complete props type including optional toggles */
export type AssetDrawerContentProps = AssetDrawerContentBaseProps &
  HideExistingProps &
  ViewModeProps;

export function AssetDrawerContent({
  sceneLabel,
  title,
  description,
  headerLayout = "stacked",
  onClose,
  accentColor = "violet",
  typeTags = [],
  selectedType = "",
  onTypeSelect,
  uploadConfig,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  renderSearchActions,
  renderBulkActions,
  renderFilters,
  selectedCount,
  onAdd,
  addButtonLabel,
  showAddButtonPrefix = true,
  children,
  titleId: titleIdProp,
  hideExisting,
  onHideExistingChange,
  hideExistingLabel,
  viewMode,
  onViewModeChange,
}: AssetDrawerContentProps) {
  const { t } = useTranslation();
  const generatedTitleId = useId();
  const titleId = titleIdProp ?? generatedTitleId;
  const accentValue = ACCENT_COLORS[accentColor];

  // Translated defaults
  const resolvedSearchPlaceholder =
    searchPlaceholder ?? t("assetDrawer.searchPlaceholder", "Search...");
  const resolvedAddButtonLabel =
    typeof addButtonLabel === "function"
      ? addButtonLabel(selectedCount)
      : (addButtonLabel ?? t("assetDrawer.addToScene", "Add to scene"));

  // Render bulk actions once to avoid double-calling
  const bulkActionsContent = renderBulkActions?.();

  // Handle drag and drop for upload box
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (uploadConfig?.onUpload && e.dataTransfer.files.length > 0) {
      uploadConfig.onUpload(e.dataTransfer.files);
    }
  };

  return (
    <div
      className="asset-drawer__content"
      style={{ "--accent-color": accentValue } as React.CSSProperties}
    >
      {/* Header */}
      <div
        className={cn(
          "asset-drawer__header",
          headerLayout === "inline" && "asset-drawer__header--inline",
        )}
      >
        <div
          className={cn(
            "asset-drawer__header-content",
            headerLayout === "inline" && "asset-drawer__header-content--inline",
          )}
        >
          {sceneLabel && (
            <span className="asset-drawer__scene-label">{sceneLabel}</span>
          )}
          {headerLayout === "inline" && sceneLabel && (
            <span className="asset-drawer__header-separator">•</span>
          )}
          <h2 id={titleId} className="asset-drawer__title">
            {title}
          </h2>
          {description && (
            <p className="asset-drawer__description">{description}</p>
          )}
        </div>
        <button
          type="button"
          className="asset-drawer__close"
          onClick={onClose}
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Body */}
      <div className="asset-drawer__body">
        {/* Type Tags */}
        {typeTags.length > 0 && onTypeSelect && (
          <div className="asset-drawer__type-section">
            <span className="asset-drawer__type-label">
              {t("assetDrawer.addAs", "ADD AS")}
            </span>
            <div className="asset-drawer__type-tags">
              {typeTags.map((tag) => (
                <button
                  key={tag.id}
                  type="button"
                  className={cn(
                    "asset-drawer__type-tag",
                    selectedType === tag.id && "asset-drawer__type-tag--active",
                  )}
                  onClick={() => onTypeSelect?.(tag.id)}
                >
                  {tag.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Upload Box */}
        {uploadConfig && (
          <div
            className="asset-drawer__upload-box"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <div className="asset-drawer__upload-icon">
              <Upload className="w-5 h-5" />
            </div>
            <div className="asset-drawer__upload-text">
              <span className="asset-drawer__upload-label">
                {uploadConfig.label}
              </span>
              {uploadConfig.sublabel && (
                <span className="asset-drawer__upload-sublabel">
                  {uploadConfig.sublabel}
                </span>
              )}
            </div>
            {uploadConfig.onGenerate && (
              <Button
                variant="outline"
                size="sm"
                className="asset-drawer__generate-btn"
                onClick={uploadConfig.onGenerate}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {uploadConfig.generateLabel ||
                  t("assetDrawer.generate", "Generate")}
              </Button>
            )}
          </div>
        )}

        {/* Search & Filter */}
        <div className="asset-drawer__search-bar">
          <div className="asset-drawer__search-input-wrapper">
            <Search className="asset-drawer__search-icon" />
            <input
              type="text"
              className="asset-drawer__search-input"
              placeholder={resolvedSearchPlaceholder}
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>
          {hideExisting !== undefined && onHideExistingChange && (
            <label className="asset-drawer__filter-toggle">
              <Checkbox
                className="rounded-[var(--radius-sm)]"
                checked={hideExisting}
                onCheckedChange={(checked) =>
                  onHideExistingChange(checked === true)
                }
              />
              <span>
                {hideExistingLabel ??
                  t("assetDrawer.hideExisting", "Hide items already in scene")}
              </span>
            </label>
          )}
          {viewMode !== undefined && onViewModeChange && (
            <ViewModeToggle mode={viewMode} onChange={onViewModeChange} />
          )}
          {renderSearchActions?.()}
        </div>

        {bulkActionsContent && (
          <div className="asset-drawer__bulk-actions">{bulkActionsContent}</div>
        )}

        {renderFilters && (
          <div className="asset-drawer__filters-row">{renderFilters()}</div>
        )}

        {/* Assets Area (scrollable) */}
        <div className="asset-drawer__assets">{children}</div>
      </div>

      {/* Footer */}
      <div className="asset-drawer__footer">
        <span className="asset-drawer__footer-hint">
          {selectedCount > 0
            ? t("assetDrawer.itemsSelected", "{{count}} item selected", {
                count: selectedCount,
              })
            : t("assetDrawer.selectItemsToAdd", "Select items to add")}
        </span>
        <div className="asset-drawer__footer-actions">
          <Button
            variant="ghost"
            className="asset-drawer__cancel-btn"
            onClick={onClose}
          >
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            className="asset-drawer__add-btn"
            onClick={onAdd}
            disabled={selectedCount === 0}
          >
            {showAddButtonPrefix && "+ "}
            {resolvedAddButtonLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
